import React, { useCallback } from "react";
import PropTypes from "prop-types";
import CookieConsentsStore, { CookieConsents } from "@whereby/jslib-commons/src/utils/CookieConsentsStore";

import { GeoService } from "@whereby/jslib-api";

import useIsSsr from "../hooks/useIsSsr";

import { readConsents } from "./helpers/cookieGeolocation";

export const CookieConsentsContext = React.createContext({});

export const FETCH_TIMEOUT_MS = 2000;

type Props = {
    children: React.ReactNode;
    geoService: GeoService;
};

const CookieConsentsContextProvider = ({ children, geoService }: Props) => {
    const isSsr = useIsSsr();
    const cookieConsentsStoreContainer = React.useRef(new CookieConsentsStore());

    const [cookieConsents, setCookieConsents] = React.useState<CookieConsents | null | undefined>();

    React.useEffect(() => {
        if (isSsr) {
            return;
        }

        return cookieConsentsStoreContainer.current.addListener((values) => {
            setCookieConsents(values);
        });
    }, [isSsr]);

    React.useEffect(() => {
        if (isSsr) {
            return;
        }

        const fetchCountry = async () => {
            const geolocation = await Promise.race([
                new Promise<undefined>((resolve) => setTimeout(resolve, FETCH_TIMEOUT_MS)),
                geoService.getGeoLocation(),
            ]).catch(() => {
                // API request failure, showing the banner as a fallback
            });

            setCookieConsents(readConsents(cookieConsentsStoreContainer.current.get(), geolocation?.countryCode));
        };

        fetchCountry().catch(console.error);
    }, [isSsr, cookieConsentsStoreContainer.current, geoService]);

    const acceptAllCookieConsents = useCallback(() => {
        cookieConsentsStoreContainer.current.set({
            strictlyNecessary: true,
            marketing: true,
            statistics: true,
            preferences: true,
        });
    }, []);

    const rejectAllCookieConsents = useCallback(() => {
        cookieConsentsStoreContainer.current.set({
            strictlyNecessary: true,
            marketing: false,
            statistics: false,
            preferences: false,
        });
    }, []);

    return (
        <CookieConsentsContext.Provider value={{ cookieConsents, acceptAllCookieConsents, rejectAllCookieConsents }}>
            {children}
        </CookieConsentsContext.Provider>
    );
};

CookieConsentsContextProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export default CookieConsentsContextProvider;
